import { Icon } from '@iconify/react'
import { useCallback, useEffect, useState } from 'react'
import { adminListUsers, adminResetPassword } from '../api'
import { useAuth } from '../components/AuthContext'

type PasswordReveal = {
  userId: string
  password: string
}

export function AdminPage() {
  const { state: authState } = useAuth()
  const [users, setUsers] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revealed, setRevealed] = useState<PasswordReveal | null>(null)
  const [resetting, setResetting] = useState<string | null>(null)

  const isAdmin = authState.status === 'authenticated' && authState.userId === 'BowuDev'

  const load = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    setError('')
    try {
      const res = await adminListUsers()
      setUsers(res.users)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    void load()
  }, [load])

  const handleReset = async (userId: string) => {
    setResetting(userId)
    setError('')
    try {
      const res = await adminResetPassword(userId)
      setRevealed({ userId: res.user_id, password: res.password })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset password')
    } finally {
      setResetting(null)
    }
  }

  const handleCopyPassword = () => {
    if (!revealed) return
    navigator.clipboard.writeText(revealed.password).catch(() => {})
  }

  if (!isAdmin) {
    return (
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Icon icon="mdi:shield-account" width="22" height="22" className="text-cyan-400" />
          <h1 className="text-xl font-semibold">Admin</h1>
        </div>
        <div className="panel text-sm text-slate-400">
          You do not have admin access.
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:shield-account" width="22" height="22" className="text-cyan-400" />
          <h1 className="text-xl font-semibold">Admin — User Management</h1>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-400/30 bg-rose-900/20 p-3 text-sm text-rose-200">
          <Icon icon="mdi:alert-circle" className="mr-1 inline" width="14" />
          {error}
        </div>
      )}

      {/* Password reveal modal */}
      {revealed && (
        <div className="mb-4 rounded-lg border border-amber-400/40 bg-amber-900/20 p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1 text-sm font-semibold text-amber-200">
              <Icon icon="mdi:alert" width="16" />
              New password for <strong>{revealed.userId}</strong>
            </p>
            <button
              className="text-xs text-slate-400 underline hover:text-white"
              onClick={() => setRevealed(null)}
            >
              Dismiss
            </button>
          </div>
          <p className="mb-2 text-xs text-amber-300/80">
            This password is shown once. Share it with the user securely.
          </p>
          <div className="relative">
            <code className="field block break-all pr-10 font-mono text-sm text-amber-100">
              {revealed.password}
            </code>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              onClick={handleCopyPassword}
              title="Copy password"
            >
              <Icon icon="mdi:content-copy" width="18" />
            </button>
          </div>
        </div>
      )}

      <div className="panel">
        {loading && <p className="text-sm text-slate-300">Loading users...</p>}
        {!loading && users.length === 0 && (
          <p className="text-sm text-slate-400">No users registered yet.</p>
        )}
        {!loading && users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-xs uppercase text-slate-400">
                  <th className="py-2 pr-4 font-medium">User ID</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((uid) => (
                  <tr key={uid} className="border-b border-slate-800 last:border-0">
                    <td className="py-2.5 pr-4 text-slate-200">
                      <div className="flex items-center gap-2">
                        {uid}
                        {uid === 'BowuDev' && (
                          <span className="rounded bg-cyan-600/20 px-1.5 py-0.5 text-xs text-cyan-300">
                            Admin
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        className="role-btn text-xs"
                        disabled={resetting === uid}
                        onClick={() => void handleReset(uid)}
                      >
                        {resetting === uid ? (
                          <span className="flex items-center gap-1">
                            <Icon icon="mdi:loading" width="12" className="animate-spin" />
                            Resetting...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Icon icon="mdi:key-variant" width="14" />
                            Reset password
                          </span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
